import * as React from "react";

// Material UI
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

// Components
import ReactCountdownClock from "react-countdown-clock";

// Stores
import { ActivityStore } from "stores";

// Utils
import { Colors } from "core/utils/Colors";
import CircleContainer from "../timer/CircleContainer";
import { Fonts } from "core/utils/Fonts";
import { strings } from "core/localizedStrings";
import Utils from "core/utils/Utils";
import * as Icons from "core/utils/Icons";
import CountdownTimerOptions from "./CountdownTimerOptions";

import SoundEffectPlayer, { SoundEffect } from "core/utils/SoundEffectPlayer";
import { Wait } from "core/utils/Wait";
import { MAX_BUTTON_FONT_SIZE } from "./Timer";

export interface CountdownTimerProps extends React.Props<any> {
    activityStore: ActivityStore;
    size?: number;
}

const CountdownTimer = (props: CountdownTimerProps) => {
    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            button: {
                fontSize: Math.min(props.size * 0.1, MAX_BUTTON_FONT_SIZE),
            },
            iconButton: {
                padding: props.size * 0.005,
                margin: props.size * 0.01,
                backgroundColor: "rgb(255,255,255,0.1)",
            },
        })
    );
    let styles = useStyles();

    const [guid, setGuid] = React.useState<string>(Utils.guid());
    const [countdownSeconds, setCountdownSeconds] = React.useState<number>(
        props.activityStore.activityOptions.timerOptions.countdownSeconds
    );
    const [countdownPaused, setCountdownPaused] = React.useState<boolean>(true);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [openSettings, setOpenSettings] = React.useState<boolean>(false);

    const handleAddSeconds = (secondsToAdd: number) => {
        let newValue = countdownSeconds + secondsToAdd;
        if (newValue > 0) {
            setCountdownSeconds(newValue);
            props.activityStore.activityOptions.timerOptions.countdownSeconds = newValue;
        } else {
            setCountdownSeconds(0);
            props.activityStore.activityOptions.timerOptions.countdownSeconds = 0;
        }
        setGuid(Utils.guid());
    };

    const handleEditClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setOpenSettings(true);
    };

    const handleStartPausedClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
        setCountdownPaused(!countdownPaused);
    };

    const handleResetClicked = (event: React.MouseEvent<HTMLButtonElement>) => {
        setCountdownPaused(true);
        setCountdownSeconds(countdownSeconds);
        setGuid(Utils.guid());
    };

    const handleSettingsClose = () => {
        setAnchorEl(null);
        setOpenSettings(false);
    };

    const handleComplete = () => {
        if (true) {
            SoundEffectPlayer.playSoundEffect(SoundEffect.POSITIVE_DING);
            Wait(200, () => {
                SoundEffectPlayer.playSoundEffect(SoundEffect.POSITIVE_DING);
                Wait(200, () => {
                    SoundEffectPlayer.playSoundEffect(SoundEffect.POSITIVE_DING);
                    Wait(200, () => {
                        SoundEffectPlayer.playSoundEffect(SoundEffect.POSITIVE_DING);
                    });
                });
            });
        }
    };

    return (
        <Grid container direction="column" alignContent="center" spacing={1}>
            <Grid item>
                <CircleContainer size={props.size}>
                    <ReactCountdownClock
                        key={props.size + guid}
                        paused={countdownPaused}
                        seconds={countdownSeconds}
                        font={Fonts.Roboto}
                        showMilliseconds={false}
                        color={Colors.darkGrey}
                        alpha={1}
                        size={props.size * 0.85}
                        onComplete={handleComplete}
                    />
                </CircleContainer>
            </Grid>
            <Grid item style={{ paddingTop: 20 }}>
                <Grid container>
                    <Grid item xs={3}>
                        <Grid container justify="flex-end">
                            <Grid item>
                                <Tooltip title={strings.reset} placement="bottom">
                                    <IconButton onClick={handleResetClicked} className={styles.iconButton}>
                                        <Icons.Replay />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={6} alignItems="center">
                        <Grid container justify="center" alignContent="center" style={{ height: "100%" }}>
                            <Grid item style={{ width: "100%" }}>
                                <Button
                                    fullWidth={true}
                                    variant="contained"
                                    color="secondary"
                                    className={styles.button}
                                    onClick={handleStartPausedClicked}
                                >
                                    {countdownPaused ? strings.start : strings.pause}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={3}>
                        <Grid container justify="flex-start">
                            <Grid item>
                                <Tooltip title={strings.edit} placement="bottom">
                                    <IconButton onClick={handleEditClicked} className={styles.iconButton}>
                                        <Icons.Edit />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Popover
                open={openSettings}
                anchorEl={anchorEl}
                onClose={handleSettingsClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <CountdownTimerOptions onAddSeconds={handleAddSeconds} />
            </Popover>
        </Grid>
    );
};

export default CountdownTimer;
